/*
 * ThryvApi.hubspotForms — custom front-end code for the HubSpot form block (thryv/hubspot-form). Guide:
 * docs/guides/hubspot-form-hooks.md.
 *
 *   ThryvApi.hubspotForms.on("prefill-from-link", {
 *     ready({ form, settings, options, root, set, check }) {}  HubSpot drew the form
 *     change({ form, name, value, input }) {},            the visitor changed a field (typing: ~300 ms after they stop)
 *     beforeSubmit({ form, values, cancel }) {},          about to submit — change fields, or cancel("why");
 *                                                         may be async: the block waits (4 s at most, then submits)
 *     refused({ form, reason, fields }) {},               the block or HubSpot refused a submit
 *     submitted({ form, values }) {},                     HubSpot accepted it
 *     schedulerOpen({ form }) {}, schedulerClose({ form }) {}   Chili Piper's scheduler window opened / closed
 *     booked({ form, meeting }) {},                       Chili Piper booked a meeting (demo scheduler)
 *     verified({ form, signals }) {},                     a Verify check answered (phone / email)
 *     scored({ form, result }) {},                        the Thryv Lead Score (debug panel only, for now)
 *     chiliPiperLead({ lead, form, options }) {},         the lead Chili Piper gets — change it, or return a new one
 *                                                         (SYNCHRONOUS: Chili Piper calls it at submit — no await)
 *   });
 *
 * A block is matched by its Hooks setting: the root's data-hooks lists the hooks it runs, in order (a name
 * per hook folder). A form running several hooks calls them in that order, at every moment. The block itself
 * announces each moment as a bubbling CustomEvent on its root (EVENTS below) — so plain listeners work too; on() is
 * those events, matched by name, with two promises kept:
 *
 *   LATE IS FINE. A hook registered after a form is drawn still gets `ready` for it, straight away.
 *   A HOOK CAN'T BREAK THE FORM. Every handler runs in a try/catch: an error is logged with the hook's name and
 *   the form carries on. An async beforeSubmit is waited for 4 s at most; a rejection or the time running out is
 *   logged and the form submits anyway — a hook never loses a lead.
 *
 * Every handler also gets `options`: this form's options for THAT hook (`{ key: "value" }`, strings).
 */
import HubspotFields from "../hubspot/fields.js";
const HubspotForms = {};

/** Event (on the block's root) → handler name. The full list of moments a hook can handle. */
const EVENTS = {
  "hubspot-form:ready": "ready",
  "hubspot-form:change": "change",
  "hubspot-form:before-submit": "beforeSubmit",
  "hubspot-form:refused": "refused",
  "hubspot-form:submitted": "submitted",
  "hubspot-form:scheduler-open": "schedulerOpen",
  "hubspot-form:scheduler-close": "schedulerClose",
  "hubspot-form:booked": "booked",
  "hubspot-form:verified": "verified",
  "hubspot-form:scored": "scored",
};

HubspotForms.EVENTS = Object.freeze({ ...EVENTS });

/** @type {Array<{name: string, handlers: object}>} */
const hooks = [];

/** The block's state the index.js keeps on its root once the form is drawn ({ form, settings }). */
const drawn = (root) => root.thryvHubspotForm || null;

/** The hooks a block runs, in order (its root's data-hooks). */
const listed = (root) => (root?.dataset.hooks || "").split(/\s+/).filter(Boolean);

/**
 * In every handler's context: `set(input, value)` and `check(input, checked)` — the only safe way to change a field.
 * HubSpot's form is React and submits its OWN copy of each field; `input.value = …` changes the page, not what
 * HubSpot sends (ThryvApi.hubspot.set).
 */
const WRITE = { set: HubspotFields.set, check: HubspotFields.check };

/** This form's options for one hook (HubspotForm::hooks(), in the block's settings). */
const options = (state, name) =>
  (state?.settings?.hooks || []).find((hook) => hook.name === name)?.options || {};

/** Run one handler, guarded: a hook's error is logged, never thrown into the form. Returns what it returned. */
const run = (name, handler, context) => {
  try {
    return handler(context);
  } catch (error) {
    console.error(`[hubspot-form hook "${name}"] ${handler.name || "handler"} failed:`, error);

    return undefined;
  }
};

/**
 * Register a hook's handlers for every block that lists `name` in its Hooks. Returns a function that removes them.
 *
 * @param {string} name
 * @param {{ready?: Function, change?: Function, beforeSubmit?: Function, refused?: Function, submitted?: Function, schedulerOpen?: Function, schedulerClose?: Function, booked?: Function, verified?: Function, scored?: Function}} handlers
 */
HubspotForms.on = function (name, handlers = {}) {
  const entry = { name: String(name), handlers };

  hooks.push(entry);

  // Late: forms already drawn get `ready` now.
  if (typeof handlers.ready === "function") {
    document.querySelectorAll(`[data-hooks~="${CSS.escape(entry.name)}"]`).forEach((root) => {
      const state = drawn(root);

      if (state)
        run(entry.name, handlers.ready, {
          root,
          ...state,
          options: options(state, entry.name),
          ...WRITE,
        });
    });
  }

  return () => {
    const index = hooks.indexOf(entry);

    if (index !== -1) hooks.splice(index, 1);
  };
};

/**
 * A PIPE, not an event: `value` passes through the `method` handler of every hook the block lists, in order — each
 * gets it as `context[key]`, may change it in place and/or return a new one (a returned object wins). Synchronous
 * only. Guarded per hook: one that throws leaves the value as it was before it (it worked on a copy). Used for
 * chiliPiperLead (the scheduler's lead for Chili Piper).
 *
 * @param  {HTMLElement} root    the block
 * @param  {string}      method  e.g. "chiliPiperLead"
 * @param  {string}      key     the value's name in the context, e.g. "lead"
 * @param  {object}      value
 * @param  {object}      extra   more context (e.g. { form })
 * @return {object}
 */
HubspotForms.pipe = function (root, method, key, value, extra = {}) {
  const state = root ? drawn(root) : null;
  let current = value;

  listed(root).forEach((name) => {
    hooks
      .filter((entry) => entry.name === name && typeof entry.handlers[method] === "function")
      .forEach((entry) => {
        const copy = { ...current };

        try {
          const result = entry.handlers[method]({
            root,
            ...state,
            ...extra,
            options: options(state, name),
            ...WRITE,
            [key]: copy,
          });

          current = result && typeof result === "object" && !Array.isArray(result) ? result : copy;
        } catch (error) {
          console.error(
            `[hubspot-form hook "${entry.name}"] ${method} failed — left as it was:`,
            error,
          );
        }
      });
  });

  return current;
};

/** Listen once per page for the block's events and hand each to the hooks that block lists — in its order. */
HubspotForms.boot = function () {
  if (HubspotForms.booted) return;

  HubspotForms.booted = true;

  Object.entries(EVENTS).forEach(([type, method]) => {
    document.addEventListener(type, (event) => {
      const root = event.target.closest?.("[data-hooks]");
      const names = listed(root);

      if (!names.length) return;

      // `verified` announces the Verify signals themselves ({ phone, email }) — handed on as `signals`.
      const detail = method === "verified" ? { signals: event.detail } : event.detail;
      const state = drawn(root);

      names.forEach((name) => {
        const context = { root, ...state, options: options(state, name), ...detail, ...WRITE };

        hooks
          .filter((entry) => entry.name === name && typeof entry.handlers[method] === "function")
          .forEach((entry) => {
            const result = run(entry.name, entry.handlers[method], context);

            // An async beforeSubmit: the block waits for it (hooks.js) — cancel() still counts until it settles.
            if (method === "beforeSubmit" && typeof result?.then === "function")
              event.detail?.wait?.(result, entry.name);
          });
      });
    });
  });
};

export { HubspotForms };
export default HubspotForms;
